import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/UI/Button';
import { useModalAction } from '@/utils/modal';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '@/components/Loading/Loading';
import { type Study, type StudyApiId, type StudyType } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useAsyncExport } from '@/support/Hooks/api/useAsyncExport';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';
import { SamplePlanTable } from '@/components/Samples/SamplePlanTable/SamplePlanTable';
import {
  type SampleDefinitionRowData,
  SamplePlanTableProvider,
  useSamplePlanTableContext,
} from '@/components/Samples/SamplePlanTable/SamplePlanTableProvider';
import { type GroupApiId, type TreatmentGroup } from '@/model/TreatmentGroup.model';
import { _isEmpty, _isNil, _notNil, getJwt } from '@/littledash';
import {
  type CollectionStatus,
  type GroupTarget,
  type PlannedAnimalTarget,
  plannedSampleExportType,
  type RegisteredAnimalTarget,
  type SampleDefinitionApiId,
} from '@/model/SamplePlan.model';
import { type Color } from '@/model/Common.model';
import { RouteService } from '@/support/RouteService';
import { errorToast } from '@/helpers';
import { type TeamGlossary } from '@/model/Glosary.model';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList from '@/components/UI/SelectDropDown/Menus/ActionList';
import { samplePlanBulkActions } from '@/components/Samples/SamplePlanTable/SamplePlanTable.utils';
import { useDispatch, useSelector } from 'react-redux';
import { isWriteUserForStudy } from '@/support/auth';
import styles from '@/components/Samples/SamplePlanTable/SamplePlanTable.module.scss';
import { isTempApiId } from '@/utils/ApiId.utils';

const BulkActionsBar: FC<{
  studyApiId: StudyApiId;
  studyStatus?: StudyType;
  navigateToSampleExecuteStepForm: (selectedSampleDefinitions: Array<SampleDefinitionRowData>) => void;
}> = ({ studyApiId, studyStatus, navigateToSampleExecuteStepForm }) => {
  const { selection, sample_definition_rows, updateRowSelection } = useSamplePlanTableContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [collectionStatus, setCollectionStatus] = useState<CollectionStatus | undefined>(undefined);

  const selectedSampleDefinitions = useMemo(() => {
    return Array.from(selection)
      .map((id) => sample_definition_rows.get(id))
      .filter(_notNil);
  }, [selection, sample_definition_rows]);

  const { invoke: fetchCollectionStatus } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/collection-status',
    path: { studyApiId },
    invokeOnInit: false,
    options: {
      onError: {
        toast: false,
        capture: true,
        throw: false,
        slug: 'sample-plan-collection-status',
      },
    },
  });

  useEffect(() => {
    if (selection.size === 0) {
      setCollectionStatus(undefined);
      return;
    }

    if (_isNil(studyApiId)) {
      setCollectionStatus(undefined);
      return;
    }

    let isCancelled = false;

    // Filter out temporary IDs as they haven't been persisted yet
    const selectedIds = Array.from(selection).filter((id): id is SampleDefinitionApiId => !isTempApiId('sdf', id));

    // If only temporary IDs are selected, don't fetch status
    if (selectedIds.length === 0) {
      setCollectionStatus(undefined);
      return;
    }

    fetchCollectionStatus({
      path: { studyApiId },
      query: { definitions: selectedIds },
    }).then((response) => {
      if (isCancelled) {
        return;
      }

      if (response.type === 'success') {
        setCollectionStatus((response.body as { status: CollectionStatus }).status);
      } else {
        setCollectionStatus('unavailable');
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [selection, studyApiId]);

  const navigateWithSampleDefinitions = useMemo(() => {
    return () => navigateToSampleExecuteStepForm(selectedSampleDefinitions);
  }, [navigateToSampleExecuteStepForm, selectedSampleDefinitions]);

  const navigateToSamplesTable = useCallback(() => {
    navigate('.', { replace: true, state: { active_tab: 'samples_table' } });
  }, [navigate]);

  const bulkActions = useMemo(() => {
    const resetSelection = () => updateRowSelection({ type: 'clear-all' });
    return samplePlanBulkActions(
      studyApiId,
      selectedSampleDefinitions,
      resetSelection,
      dispatch,
      navigateWithSampleDefinitions,
      navigateToSamplesTable,
      studyStatus,
      collectionStatus
    );
  }, [
    studyApiId,
    selectedSampleDefinitions,
    updateRowSelection,
    dispatch,
    navigateWithSampleDefinitions,
    navigateToSamplesTable,
    studyStatus,
    collectionStatus,
  ]);

  return (
    <div className="flex items-center">
      {selection.size > 0 && (
        <div className={`${styles.SelectionBadge} mid-gray bg-light-gray ph3 ba b--moon-gray f6 br-0 br--left br1`}>
          {selection.size} selected
        </div>
      )}
      <SelectDropDown
        title="Bulk actions"
        disabled={selection.size === 0}
        testId="bulk-actions-dropdown"
        className={`plain f6 ${selection.size > 0 ? 'br--right' : ''}`}
        alignMenu="right"
        btnProps={{ plain: true }}
      >
        <ActionList actions={bulkActions} testPrefix="bulk-actions" />
      </SelectDropDown>
    </div>
  );
};

const samplePlanFeatureSelector = createSelector([featuresSelector], (features) => ({
  asyncExportsEnabled: features.async_exports === true,
}));

export const SamplePlanTableView: FC = () => {
  const { id: studyId } = useParams();
  const { openModal } = useModalAction();
  const navigate = useNavigate();
  const [selectedSamplePlans, updateSelectedSamplePlans] = useState<Set<SampleDefinitionApiId>>(new Set());
  const { asyncExportsEnabled } = useSelector(samplePlanFeatureSelector);
  const triggerAsyncExport = useAsyncExport();
  const { invoke: createSamplingPlanExport } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/exports/sampling-plan',
    options: { invokeOnInit: false, onError: { toast: false, throw: false, slug: 'sampling-plan-async-export' } },
  });

  const navigateToSampleExecuteStepForm = (selectedSampleDefinitions: Array<SampleDefinitionRowData>) => {
    const sampleDefinitionIds = selectedSampleDefinitions.map((def) => def.api_id);

    const baseRoute = RouteService.web({
      route: 'studies.samples.execute',
      path: { id: studyId },
    }).route;

    navigate(baseRoute, { state: { sampleDefinitionIds } });
  };

  const { response: studyResponse, loading: studyLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.show.p',
    path: { id: studyId },
    options: { invokeOnInit: true, onError: { slug: 'sample-plans-study-load' } },
    query: { include: ['users'] },
  });
  const { response: groupsResponse, loading: groupsLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: studyId },
    query: { perPage: -1 },
  });
  const { response: sampleMetadataGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'sample_meta',
    },
    invokeOnInit: true,
    options: { onError: { slug: 'sample-meta-load' } },
  });
  const { response: sampleGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'samples',
      perPage: -1,
      order: 'asc',
      sort: 'title',
    },
    options: { onError: { slug: 'samples-glossary-load' } },
  });

  const {
    loading: samplePlanLoading,
    response: samplePlanResponse,
    invoke: loadSamplePlan,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples',
    path: { studyApiId: 'sdy_invalid' },
    invokeOnInit: false,
    options: { onError: { slug: 'sample-plans-table-load-sample-plan' } },
  });
  const {
    loading: registeredAnimalsLoading,
    response: registeredAnimalsResponse,
    invoke: loadRegisteredAnimals,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/animals',
    path: { studyId: 'sdy_invalid' },
    query: { perPage: -1, include: ['study'] },
    invokeOnInit: false,
    options: { onError: { slug: 'sample-plans-table-load-registered-animals' } },
  });
  const {
    loading: plannedAnimalsLoading,
    response: plannedAnimalsResponse,
    invoke: loadPlannedAnimals,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/animals',
    path: { studyApiId: 'sdy_invalid' },
    query: { perPage: -1 },
    invokeOnInit: false,
    options: { onError: { slug: 'sample-plans-table-load-planned-animals' } },
  });
  useEffect(() => {
    if (studyResponse?.type === 'success') {
      const studyApiId = (studyResponse.body as { data: Study }).data.api_id;
      loadSamplePlan({ path: { studyApiId } });
      loadPlannedAnimals({ path: { studyApiId }, query: { perPage: -1 } });
      loadRegisteredAnimals({ path: { studyId: studyApiId }, query: { perPage: -1, include: ['study_group'] } });
    }
  }, [studyResponse]);

  const tableData = useMemo(() => {
    if (
      groupsResponse?.type === 'success' &&
      registeredAnimalsResponse?.type === 'success' &&
      plannedAnimalsResponse?.type === 'success'
    ) {
      const groups = (
        groupsResponse.body as {
          data: Array<TreatmentGroup>;
        }
      ).data.map<GroupTarget>((group) => ({
        api_id: group.api_id as GroupApiId,
        name: group.name,
        no: group.no,
        color: group.color as Color,
        max_subjects: group.max_subjects,
      }));
      const registered_animals = registeredAnimalsResponse.body.data.map<RegisteredAnimalTarget>((animal) => ({
        api_id: animal.api_id,
        name: animal.name,
        study_group_api_id: animal.study_group?.api_id ?? null,
      }));

      return { groups, registered_animals };
    }
    return null;
  }, [groupsResponse, registeredAnimalsResponse, plannedAnimalsResponse]);

  if (
    studyLoading ||
    samplePlanLoading ||
    groupsLoading ||
    registeredAnimalsLoading ||
    plannedAnimalsLoading ||
    studyResponse?.type !== 'success' ||
    samplePlanResponse?.type !== 'success' ||
    groupsResponse?.type !== 'success' ||
    registeredAnimalsResponse?.type !== 'success' ||
    plannedAnimalsResponse?.type !== 'success' ||
    _isNil(tableData)
  ) {
    return <Loading />;
  }
  const study = (studyResponse.body as { data: Study }).data;
  const handleEdit = () => {
    openModal('ADD_SAMPLE_PLANS', {
      studyId: study.id,
      studyApiId: study.api_id,
      onSampleDefinitionsUpdate: () =>
        loadSamplePlan({
          path: {
            studyApiId: (
              studyResponse.body as {
                data: Study;
              }
            ).data.api_id,
          },
        }),
      sampleDefinitions: samplePlanResponse.body.definitions,
    });
  };

  const handleExport = async () => {
    if (asyncExportsEnabled) {
      await triggerAsyncExport(() => createSamplingPlanExport({ path: { studyApiId: study.api_id } }));
      return;
    }

    try {
      const apiRoute = RouteService.api({
        endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/export/{type}',
        path: {
          studyApiId: study.api_id,
          type: plannedSampleExportType.sample_plan,
        },
      });
      const response = await fetch(
        new Request(apiRoute.url, {
          method: apiRoute.method,
          headers: {
            Authorization: `Bearer ${getJwt()}`,
          },
        })
      );

      if (!response.ok) {
        errorToast('Failed to export sample plan.');
        return;
      }

      const filename =
        /filename="(?<filename>[a-zA-Z0-9_.-]+)"/.exec(response.headers.get('content-disposition') ?? '')?.groups
          ?.filename ?? 'sampling-plan.csv';
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (error) {
      errorToast('Failed to export sample plan.');
    }
  };

  if (_isEmpty(samplePlanResponse.body.definitions)) {
    return (
      <div className="flex flex-column" data-test-component="SamplePlanTableView" data-test-element="empty-state">
        <div className="ui-card mh3">
          <div className="flex flex-column items-center bg-light-gray ma4 pa4">
            <div className="f4 lh-copy">No Sample Plan</div>
            <div className="mv4">A preview of your sample plan will appear here</div>
            {isWriteUserForStudy(study) && (
              <a className="link blue" data-testid="add-sample-plan" onClick={() => handleEdit()}>
                Add sample plan
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ui-card" data-test-component="SamplePlanTableView" data-test-element="container">
      <SamplePlanTableProvider
        sample_definitions={samplePlanResponse.body.definitions}
        groups={tableData.groups}
        registered_animals={tableData.registered_animals}
        planned_animals={plannedAnimalsResponse.body.data as Array<PlannedAnimalTarget>}
        metadata_glossary={sampleMetadataGlossaryResponse?.body ?? []}
        sample_glossary={(sampleGlossaryResponse?.body as { data?: Array<TeamGlossary> })?.data ?? []}
        selection={selectedSamplePlans}
        onSelectionChange={(value) => updateSelectedSamplePlans(value as Set<SampleDefinitionApiId>)}
        displaySampleCounts={true}
      >
        <div className="flex flex-row ma3">
          {isWriteUserForStudy(study) && (
            <BulkActionsBar
              studyApiId={study.api_id}
              studyStatus={study.status}
              navigateToSampleExecuteStepForm={navigateToSampleExecuteStepForm}
            />
          )}
          <div className="flex flex-row ml-auto">
            {isWriteUserForStudy(study) && (
              <Button plain onClick={() => handleEdit()}>
                Edit
              </Button>
            )}
            <Button primary className="ml2" onClick={() => handleExport()}>
              Export
            </Button>
          </div>
        </div>
        <SamplePlanTable />
      </SamplePlanTableProvider>
    </div>
  );
};
